import React, { useEffect } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

interface ConfirmCancelHotkeyDialogProps {
  open: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  title?: string;
  message?: string;
}

/**
 * Alerta de confirmación para ESC-Cancelar.
 * Enter confirma siempre (aunque el foco esté en Volver); Esc cierra y sigue en la pantalla.
 */
const ConfirmCancelHotkeyDialog: React.FC<ConfirmCancelHotkeyDialogProps> = ({
  open,
  onConfirm,
  onDismiss,
  title = 'Cancelar',
  message = '¿Está seguro que desea salir? Se perderán los datos no guardados.',
}) => {
  const theme = useTheme();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      if (event.key !== 'Enter' && event.code !== 'Enter' && event.code !== 'NumpadEnter') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onConfirm();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onConfirm]);

  return (
    <Dialog
      open={open}
      onClose={onDismiss}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-cancel-hotkey-title"
    >
      <DialogTitle id="confirm-cancel-hotkey-title" sx={{ fontWeight: 600, pb: 1 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button type="button" onClick={onDismiss} color="inherit" sx={{ textTransform: 'none' }}>
          Volver
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={onConfirm}
          autoFocus
          sx={{ textTransform: 'none' }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <span>Confirmar</span>
            <Chip
              label="ENTER"
              size="small"
              sx={{
                height: 20,
                bgcolor: alpha(theme.palette.common.white, 0.22),
                color: 'inherit',
                fontWeight: 700,
                fontSize: '0.65rem',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmCancelHotkeyDialog;
